import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  FormFeedback,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  fetchFileEditions,
  updateFile,
} from '../../actions/Files';

import {
  getShare,
  userCan,
} from '../../helpers/Auth';
import {
  eventSubscribe,
  eventUnsubscribe,
} from '../../helpers/Events';

import useOutsideClick from '../../hooks/useOutsideClick';

const FileNameEditableField = ({
  dispatch,
  file,
  field,
  fieldType,
  fileModalOpen,
  required,
}) => {
  const intl = useIntl();

  const elRef = useRef();
  const containerRef = useRef();
  const fieldRef = useRef();
  const [editMode, setEditMode] = useState(false);
  const [editModeProcess, setEditModeProcess] = useState(null);
  const [nameValue, setNameValue] = useState(file.name || file.original_name);
  const [prefixValue, setPrefixValue] = useState(file.attributes?.prefix || '');
  const [suffixValue, setSuffixValue] = useState(file.attributes?.suffix || '');
  const [touchedName, setTouchedName] = useState(false);
  const [touchedPrefix, setTouchedPrefix] = useState(false);
  const [touchedSuffix, setTouchedSuffix] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [validationError, setValidationError] = useState('');

  const editDisabled = !userCan('file.edit', file) || getShare();
  const isFieldInvalid = editMode && !!(error || validationError);

  const openEdit = (e, process = null) => {
    if (e) {
      e.preventDefault();
    }

    setEditModeProcess(process);

    if (!saving && !editDisabled) {
      setEditMode(true);
    }
  };

  useEffect(() => {
    if (editMode) {
      if (fieldRef.current) {
        fieldRef.current.focus();
      }
    }
  }, [editMode]);

  useEffect(() => {
    setNameValue(file.name || file.original_name);
  }, [file.name, file.original_name]);

  useEffect(() => {
    setPrefixValue(file.attributes?.prefix);
  }, [file.attributes?.prefix]);

  useEffect(() => {
    setSuffixValue(file.attributes?.suffix);
  }, [file.attributes?.suffix]);

  const saveChanges = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (required && !nameValue) {
      setError(true);
      return;
    }

    if (
      (!process && !touchedName) ||
      (process === 'prefix' && !touchedPrefix) ||
      (process === 'suffix' && !touchedSuffix)
    ) {
      setEditMode(false);
      setEditModeProcess(null);
      return;
    }

    setError(false);
    setValidationError('');
    setEditMode(false);
    setEditModeProcess(null);
    setSaving(true);
    const editedFile = { ...file };
    editedFile.name = nameValue;
    editedFile.prefix = prefixValue;
    editedFile.suffix = suffixValue;
    editedFile.inline_edit = true;
    dispatch(updateFile(editedFile)).then((response) => {
      setSaving(false);
      setTouchedName(false);
      setTouchedPrefix(false);
      setTouchedSuffix(false);
      setEditMode(false);
      if (response.parent_uuid && fileModalOpen) {
        dispatch(fetchFileEditions(editedFile.uuid));
      }
    });
  }, [
    dispatch,
    file,
    fileModalOpen,
    nameValue,
    prefixValue,
    required,
    suffixValue,
    touchedName,
    touchedPrefix,
    touchedSuffix,
  ]);

  useEffect(() => {
    const onFileSwitch = () => {
      if (editMode) {
        saveChanges(null, true);
      }
    };

    eventSubscribe('fileSwitch', onFileSwitch);
    return () => {
      eventUnsubscribe('fileSwitch', onFileSwitch);
    };
  }, [saveChanges, editMode]);

  const cancelChanges = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setNameValue(file.name || file.original_name);
    setPrefixValue(file.attributes?.prefix || '');
    setSuffixValue(file.attributes?.suffix || '');
    setEditMode(false);
    setEditModeProcess(null);
  };

  const handleNameChange = (e) => {
    setTouchedName(e.target.value !== nameValue);
    setNameValue(e.target.value);
  };

  const handlePrefixChange = (e) => {
    setTouchedPrefix(e.target.value !== prefixValue);
    setPrefixValue(e.target.value);
  };

  const handleSuffixChange = (e) => {
    setTouchedSuffix(e.target.value !== suffixValue);
    setSuffixValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && fieldType !== 'textarea') {
      saveChanges();
    }

    if (e.key === 'Escape') {
      cancelChanges();
    }
  };

  const outsideClickHandler = (e) => {
    const { target } = e;

    const classesToExclude = [
      'fas',
      'flatpickr-monthDropdown-months',
      'flatpickr-prev-month',
      'flatpickr-next-month',
      'cur-year',
      'arrowUp',
      'arrowDown',
    ];
    const targetClasses = [...target.classList];

    if (
      editMode &&
      !targetClasses.some(r => classesToExclude.includes(r)) &&
      target.tagName !== 'path' &&
      target.tagName !== 'svg'
    ) {
      saveChanges();
    }
  };

  useOutsideClick(containerRef, outsideClickHandler);

  if (!nameValue && (!userCan('file.edit', file) || getShare())) {
    return null;
  }

  return (
    <>
      <div className={classnames('file-details-row file-details-row-file-name', { editing: editMode })}>
        <span className="file-details-row-file-name-content" ref={containerRef}>
          {file.attributes?.prefix && (
            <span
              className={classnames(
                'editable-field',
                `editable-field-${fieldType}`,
                'editable-field-file-prefix',
                {
                  'editable-field-editing': (editMode && editModeProcess === 'prefix'),
                  'editable-field-disabled': editDisabled,
                  'editable-field-empty': !prefixValue,
                },
              )}
              role="button"
              tabIndex={0}
              title={(editMode || editDisabled) ? '' :
                intl.formatMessage({ defaultMessage: 'Click to edit prefix' })}
              onKeyUp={(e) => openEdit(e, 'prefix')}
              onClick={(e) => openEdit(e, 'prefix')}
            >
              {(editMode && editModeProcess === 'prefix') ? (
                <Input
                  type="text"
                  name={field}
                  defaultValue={file.attributes.prefix}
                  disabled={saving}
                  onChange={handlePrefixChange}
                  onKeyDown={handleKeyDown}
                  invalid={isFieldInvalid}
                />
              ) : (
                prefixValue
              )}
            </span>
          )}
          <span
            className={classnames(
              'editable-field',
              `editable-field-${fieldType}`,
              `editable-field-file-name`,
              {
                'editable-field-editing': (editMode && !editModeProcess),
                'editable-field-disabled': editDisabled,
                'editable-field-empty': !nameValue,
                'editable-field-no-margin-left': file.attributes?.prefix,
                'editable-field-no-margin-right': file.attributes?.suffix,
              },
            )}
            role="button"
            tabIndex={0}
            title={(editMode || editDisabled) ? '' :
              intl.formatMessage({ defaultMessage: 'Click to edit file name' })}
            ref={elRef}
            onKeyUp={openEdit}
            onClick={openEdit}
          >
            {(editMode && !editModeProcess) ? (
              <Input
                type="text"
                innerRef={fieldRef}
                name={field}
                defaultValue={nameValue}
                disabled={saving}
                onChange={handleNameChange}
                onKeyDown={handleKeyDown}
                invalid={isFieldInvalid}
              />
            ) : (
              <>
                {nameValue}
              </>
            )}
          </span>
          {file.attributes?.suffix && (
            <span
              className={classnames(
                'editable-field',
                `editable-field-${fieldType}`,
                'editable-field-file-suffix',
                {
                  'editable-field-editing': (editMode && editModeProcess === 'suffix'),
                  'editable-field-disabled': editDisabled,
                  'editable-field-empty': !suffixValue,
                },
              )}
              role="button"
              tabIndex={0}
              title={(editMode || editDisabled) ? '' :
                intl.formatMessage({ defaultMessage: 'Click to edit file suffix' })}
              onKeyUp={(e) => openEdit(e, 'suffix')}
              onClick={(e) => openEdit(e, 'suffix')}
            >
              {(editMode && editModeProcess === 'suffix') ? (
                <Input
                  type="text"
                  name={field}
                  defaultValue={file.attributes.suffix}
                  disabled={saving}
                  onChange={handleSuffixChange}
                  onKeyDown={handleKeyDown}
                  invalid={isFieldInvalid}
                />
              ) : (
                suffixValue
              )}
            </span>
          )}
        </span>
        {editMode && (
          <span className="file-details-row-file-name-buttons">
            <a
              href="#save"
              title={intl.formatMessage({ defaultMessage: 'Save' })}
              onClick={saveChanges}
              className="mr-2"
            >
              <i className="fas fa-check" />
            </a>
            <a
              href="#cancel"
              title={intl.formatMessage({ defaultMessage: 'Cancel' })}
              onClick={cancelChanges}
            >
              <i className="fas fa-xmark" />
            </a>
          </span>
        )}
      </div>
      {isFieldInvalid && (
        <FormFeedback style={{ display: 'block' }}>
          {validationError || intl.formatMessage({ defaultMessage: 'File name field is required' })}
        </FormFeedback>
      )}
    </>
  );
};

const mapStateToProps = state => ({
  fileModalOpen: state.Files.fileModalOpen,
});

FileNameEditableField.propTypes = {
  dispatch: PropTypes.func.isRequired,
  file: PropTypes.object.isRequired,
  field: PropTypes.string.isRequired,
  fieldType: PropTypes.string,
  fileModalOpen: PropTypes.bool.isRequired,
  required: PropTypes.bool,
};

FileNameEditableField.defaultProps = {
  fieldType: 'text',
  required: false,
};

export default connect(mapStateToProps)(FileNameEditableField);
